import * as Portal from '@radix-ui/react-portal';
import { useState } from 'react';
import { useForm, usePage } from '@inertiajs/react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  Flex,
  Select,
  Separator,
  Text,
  TextArea,
} from '@radix-ui/themes';

import { SupportIcon } from '@/components/icons';
import Button from '@/components/Button';
import ErrorCallout from '@/components/ErrorCallout';
import FormField from '@/components/FormField';
import { firm_support_requests_path } from '@/lib/routes';

const CATEGORIES = ['issue', 'feature_request', 'question', 'other'];

export default function Support() {
  const { t } = useTranslation();
  const { component, props } = usePage();
  const { current_user, support_email, firm } = props;
  const [open, setOpen] = useState(false);

  const {
    data,
    setData,
    post,
    processing,
    transform,
    reset,
    errors,
    setError,
    clearErrors,
  } = useForm({
    subject: t('support.default_subject'),
    category: 'issue',
  });

  const handleChange = ({ target: { name, value } }) => setData(name, value);

  function handleOpenChange(isOpened) {
    if (isOpened) {
      reset();
      clearErrors();
    }
    setOpen(isOpened);
  }

  function submit(event) {
    event.preventDefault();

    clearErrors();

    transform((data) => ({ ...data, component }));

    post(firm_support_requests_path(firm.slug), {
      preserveUrl: true,
      only: ['flash', 'errors'],
      onSuccess: () => {
        reset();
        setOpen(false);
      },
      onError: setError,
    });
  }

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Trigger>
        <Button variant="ghost" size="3" radius="full">
          <SupportIcon width="16" height="16" />
          <Text className="hidden xl:inline">{t('common.support')}</Text>
        </Button>
      </Dialog.Trigger>
      <Portal.Root>
        <Dialog.Content maxWidth="500px">
          <Dialog.Title>{t('support.title')}</Dialog.Title>
          <Dialog.Description size="2" mb="4">
            {t('support.description')}
          </Dialog.Description>

          <form onSubmit={submit} autoComplete="off">
            <Flex direction="column" gap="4">
              <ErrorCallout
                show={Object.keys(errors).length}
                text={errors.base || t('common.fix_errors_to_continue')}
              />

              <Flex gap="4" className="rounded-md bg-gray-100 p-3">
                <Flex direction="column" gap="1" className="flex-1">
                  <Text size="1" color="gray" className="uppercase">
                    {t('support.fields.to')}
                  </Text>
                  <Text size="2">{support_email}</Text>
                </Flex>

                <Separator orientation="vertical" className="h-auto" />

                <Flex direction="column" gap="1" className="flex-1">
                  <Text size="1" color="gray" className="uppercase">
                    {t('support.fields.from')}
                  </Text>
                  <Text size="2">{current_user.email}</Text>
                </Flex>
              </Flex>

              <FormField
                required
                field="subject"
                label={t('support.fields.subject')}
                value={data.subject}
                onChange={handleChange}
                error={errors.subject}
              />

              <FormField
                required
                field="category"
                label={t('support.fields.category')}
                error={errors.category}
              >
                <Select.Root
                  size="3"
                  value={data.category}
                  onValueChange={(value) => setData('category', value)}
                >
                  <Select.Trigger id="category" className="w-full" />
                  <Select.Content>
                    {CATEGORIES.map((category) => (
                      <Select.Item key={category} value={category}>
                        {t(`support.categories.${category}`)}
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Root>
              </FormField>

              <FormField
                required
                field="message"
                label={t('support.fields.message')}
                error={errors.message}
              >
                <TextArea
                  required
                  id="message"
                  name="message"
                  size="3"
                  value={data.message}
                  onChange={handleChange}
                  placeholder={t('support.message_placeholder')}
                  style={{ minHeight: 120 }}
                />
              </FormField>

              <Flex gap="3" mt="2" justify="end">
                <Dialog.Close>
                  <Button variant="soft" color="gray">
                    {t('common.cancel')}
                  </Button>
                </Dialog.Close>

                <Button
                  type="submit"
                  disabled={processing}
                  loading={processing}
                >
                  {t('common.submit')}
                </Button>
              </Flex>
            </Flex>
          </form>
        </Dialog.Content>
      </Portal.Root>
    </Dialog.Root>
  );
}
